import React, { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "@/components/utils/utils";

export interface ClientFilterProps {
  selectedClient: string | null;
  onClientChange: (client: string | null) => void;
  itineraries: { contactFullName: string; contactEmail: string }[];
  disabled?: boolean;
}

export const ClientFilter: React.FC<ClientFilterProps> = ({
  selectedClient,
  onClientChange,
  itineraries,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const clients = useMemo(() => {
    const clientMap = new Map<string, { name: string; email: string }>();
    itineraries.forEach((it) => {
      const email = it.contactEmail;
      const name = it.contactFullName;
      if (email && !clientMap.has(email)) {
        clientMap.set(email, { name, email });
      }
    });
    return Array.from(clientMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [itineraries]);

  const filteredClients = clients.filter((c) => {
    const s = searchTerm.toLowerCase();
    return c.name.toLowerCase().includes(s) || c.email.toLowerCase().includes(s);
  });

  const handleSelect = (email: string) => {
    onClientChange(selectedClient === email ? null : email);
    setIsOpen(false);
    setSearchTerm("");
  };

  const selectedLabel = selectedClient
    ? (clients.find((c) => c.email === selectedClient)?.name ?? selectedClient)
    : "Client";

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          disabled={disabled}
          className="flex items-center gap-2 min-w-[140px] justify-between"
        >
          <span className="truncate">{selectedLabel}</span>
          <div className="flex items-center gap-1">
            <ChevronDown className="h-4 w-4" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="start">
        <div className="p-3">
          <Input
            placeholder="Search client..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="mb-2"
          />
          <div className="max-h-48 overflow-y-auto">
            {filteredClients.length > 0 ? (
              filteredClients.map((client) => (
                <button
                  key={client.email}
                  className={cn(
                    "w-full text-left px-3 py-2 hover:bg-gray-100 flex items-center justify-between",
                    selectedClient === client.email && "bg-blue-50"
                  )}
                  onClick={() => handleSelect(client.email)}
                >
                  <div className="flex flex-col">
                    <span className="font-medium">{client.name}</span>
                    <span className="text-sm text-gray-500">{client.email}</span>
                  </div>
                  {selectedClient === client.email && (
                    <Check className="h-4 w-4 text-blue-600" />
                  )}
                </button>
              ))
            ) : (
              <div className="px-3 py-2 text-gray-500">No clients found</div>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
